import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ResolvedAuthenticatedReloadPreview } from '../plugin-system/authenticated-reload-preview-types.ts';
import './authenticated-reload-preview-handoff.css';

interface AuthenticatedReloadPreviewHandoffValue {
  pending: boolean;
  markReady: (previewId: string) => void;
}

const AuthenticatedReloadPreviewHandoffContext =
  createContext<AuthenticatedReloadPreviewHandoffValue | null>(null);

export function useAuthenticatedReloadPreviewHandoffPending(): boolean {
  return useContext(AuthenticatedReloadPreviewHandoffContext)?.pending ?? false;
}

export function AuthenticatedReloadPreviewReady({ previewId }: { previewId: string }) {
  const handoff = useContext(AuthenticatedReloadPreviewHandoffContext);
  useLayoutEffect(() => handoff?.markReady(previewId), [handoff, previewId]);
  return null;
}

export default function AuthenticatedReloadPreviewHandoff({
  children,
  preview,
}: {
  children: ReactNode;
  preview: ResolvedAuthenticatedReloadPreview;
}) {
  const [pending, setPending] = useState(true);
  const previewElementRef = useRef<HTMLDivElement | null>(null);
  const markReady = useCallback(
    (previewId: string) => {
      if (previewId === preview.id) setPending(false);
    },
    [preview.id],
  );
  const value = useMemo(() => ({ markReady, pending }), [markReady, pending]);

  useLayoutEffect(() => {
    const previewElement = previewElementRef.current;
    if (!pending || !previewElement) return undefined;
    const dismissEmptyPreview = () => {
      if (!previewElement.hasChildNodes()) setPending(false);
    };
    dismissEmptyPreview();
    if (!previewElement.hasChildNodes()) return undefined;
    const observer = new MutationObserver(dismissEmptyPreview);
    observer.observe(previewElement, { childList: true });
    return () => observer.disconnect();
  }, [pending, preview.content]);

  return (
    <AuthenticatedReloadPreviewHandoffContext.Provider value={value}>
      {pending && (
        <div className="authenticated-reload-preview-handoff" ref={previewElementRef}>
          {preview.content}
        </div>
      )}
      {children}
    </AuthenticatedReloadPreviewHandoffContext.Provider>
  );
}
